(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var CART_ENDPOINTS = {
    cart: '/cart.js',
    add: '/cart/add.js',
    change: '/cart/change.js'
  };

  var drawer = null;
  var sectionRoot = null;
  var abortController = null;
  var lastFocusedElement = null;
  var cartState = null;
  var activeCurrency = null;
  var isOpen = false;
  var upsellTrack = null;
  var upsellDotsContainer = null;

  function getActiveCurrency() {
    if (cartState && cartState.currency) return cartState.currency;
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function qsa(selector, root) {
    return (root || document).querySelectorAll(selector);
  }

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function getFocusableElements(container) {
    if (!container) return [];
    var selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    var nodes = container.querySelectorAll(selectors);
    var visible = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement) {
        visible.push(el);
      }
    }
    return visible;
  }

  function trapFocus(event) {
    if (!isOpen || !drawer) return;
    if (event.key !== 'Tab' && event.keyCode !== 9) return;
    var panel = qs('.oddit-cart-drawer__panel', drawer);
    var focusables = getFocusableElements(panel);
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        last.focus();
        event.preventDefault();
      }
    } else {
      if (document.activeElement === last) {
        first.focus();
        event.preventDefault();
      }
    }
  }

  function onKeydown(event) {
    if (!isOpen) return;
    if (event.key === 'Escape' || event.keyCode === 27) {
      close();
      return;
    }
    trapFocus(event);
  }

  function lockBodyScroll() {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('oddit-cart-drawer-open');
  }

  function unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.classList.remove('oddit-cart-drawer-open');
  }

  function open() {
    if (!drawer || isOpen) return;
    lastFocusedElement = document.activeElement;
    drawer.style.display = '';
    // Force reflow so transition runs
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    isOpen = true;
    lockBodyScroll();
    var panel = qs('.oddit-cart-drawer__panel', drawer);
    var focusables = getFocusableElements(panel);
    if (focusables.length > 0) {
      try { focusables[0].focus(); } catch (e) {}
    }
    // Refresh cart contents on open to keep state in sync
    refreshCart().catch(function () {});
  }

  function close() {
    if (!drawer || !isOpen) return;
    drawer.classList.remove('is-open');
    isOpen = false;
    unlockBodyScroll();
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      try { lastFocusedElement.focus(); } catch (e) {}
    }
  }

  function toggle() {
    if (isOpen) close();
    else open();
  }

  // ----- Cart API ----------------------------------------------------------

  function jsonHeaders() {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };
  }

  function parseJsonResponse(response) {
    return response.text().then(function (text) {
      var data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }
      if (!response.ok) {
        var err = new Error((data && (data.description || data.message)) || ('Cart request failed: ' + response.status));
        err.status = response.status;
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  function getCart() {
    return fetch(CART_ENDPOINTS.cart, {
      method: 'GET',
      headers: jsonHeaders(),
      credentials: 'same-origin'
    }).then(parseJsonResponse);
  }

  function addToCart(variantId, quantity, properties) {
    var body = {
      id: Number(variantId),
      quantity: quantity == null ? 1 : Number(quantity)
    };
    if (properties && typeof properties === 'object') {
      body.properties = properties;
    }
    return fetch(CART_ENDPOINTS.add, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
      .then(parseJsonResponse)
      .then(function () { return getCart(); })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function addMultipleToCart(items) {
    var normalized = (items || []).map(function (item) {
      var entry = {
        id: Number(item.id),
        quantity: item.quantity == null ? 1 : Number(item.quantity)
      };
      if (item.properties && typeof item.properties === 'object') {
        entry.properties = item.properties;
      }
      if (item.selling_plan) {
        entry.selling_plan = item.selling_plan;
      }
      return entry;
    });
    return fetch(CART_ENDPOINTS.add, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({ items: normalized })
    })
      .then(parseJsonResponse)
      .then(function () { return getCart(); })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetch(CART_ENDPOINTS.change, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItemKey, quantity: Number(quantity) })
    })
      .then(parseJsonResponse)
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return fetch(CART_ENDPOINTS.change, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineItemKey,
        selling_plan: sellingPlanId == null ? null : Number(sellingPlanId)
      })
    })
      .then(parseJsonResponse)
      .then(function (cart) {
        renderCart(cart);
        return cart;
      });
  }

  // Same as updateLineItemToSubscription but WITHOUT re-rendering, so several line
  // items can be changed in sequence (cart/change.js mutations must be serialized)
  // and the cart rendered once from the final response.
  function changeSellingPlanRaw(lineItemKey, sellingPlanId) {
    return fetch(CART_ENDPOINTS.change, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineItemKey,
        selling_plan: sellingPlanId == null ? null : Number(sellingPlanId)
      })
    }).then(parseJsonResponse);
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  // ----- Render ------------------------------------------------------------

  function buildLineItemHtml(item) {
    var key = escapeHtml(item.key);
    // Match the product card: drop the size suffix in parentheses, e.g. "Dr. Perfy (12-pack)" -> "Dr. Perfy"
    var rawTitle = (item.product_title || (item.product && item.product.title) || item.title || '');
    var title = escapeHtml(rawTitle.split('(')[0].trim());
    var variantTitle = item.variant_title ? escapeHtml(item.variant_title) : '';
    var url = escapeHtml(item.url || '#');
    var image = item.image || (item.featured_image && item.featured_image.url) || '';
    var imageAlt = escapeHtml(title);
    var price = formatMoney(item.final_price != null ? item.final_price : item.price);
    var removeLabel = escapeHtml('Remove ' + title + ' from cart');

    var imageHtml = '';
    if (image) {
      // Convert to small responsive size if it's a shopify CDN url
      var imgSrc = image;
      if (typeof imgSrc === 'string' && imgSrc.indexOf('//') === 0) {
        imgSrc = window.location.protocol + imgSrc;
      }
      imageHtml = '<img class="oddit-cart-drawer__item-image" src="' + escapeHtml(imgSrc) + '" alt="' + imageAlt + '" loading="lazy" width="90" height="90">';
    }

    // Prefer the "number of cans" descriptor (perfy.number_of_cans, via the
    // Liquid-built lookup, since cart.js omits metafields); fall back to variant.
    var cans = (window.OdditCartCans && item.product_id != null)
      ? window.OdditCartCans[item.product_id]
      : null;
    var variantHtml;
    if (cans != null && cans !== '') {
      variantHtml = '<p class="oddit-cart-drawer__item-variant">' + cans + (Number(cans) === 1 ? ' CAN' : ' CANS') + '</p>';
    } else if (variantTitle) {
      variantHtml = '<p class="oddit-cart-drawer__item-variant">' + variantTitle + '</p>';
    } else {
      variantHtml = '';
    }

    // Subscription / one-time indicator. /cart.js exposes selling_plan_allocation on
    // subscribed lines; OdditCartSellingPlans (built in Liquid) tells us whether a
    // one-time line *could* be subscribed, so we only label "One-time purchase" when
    // a subscription is actually available for that variant.
    var allocation = item.selling_plan_allocation;
    var hasAllocation = !!(allocation && allocation.selling_plan);
    var availablePlan = (window.OdditCartSellingPlans && item.variant_id != null)
      ? window.OdditCartSellingPlans[item.variant_id]
      : null;
    var planOptions = (window.OdditCartSellingPlanOptions && item.variant_id != null)
      ? window.OdditCartSellingPlanOptions[item.variant_id]
      : null;
    var planHtml;
    if (hasAllocation) {
      var currentPlanId = allocation.selling_plan.id;
      if (planOptions && planOptions.length >= 2) {
        var optsHtml = '';
        for (var po = 0; po < planOptions.length; po++) {
          var opt = planOptions[po];
          var selectedAttr = String(opt.id) === String(currentPlanId) ? ' selected' : '';
          var optLabel = String(opt.label || 'Subscription').replace(/^\s*delivery every\s+/i, '').replace(/^\s*every\s+/i, '');
          optsHtml += '<option value="' + escapeHtml(String(opt.id)) + '"' + selectedAttr + '>' + escapeHtml(optLabel) + '</option>';
        }
        planHtml =
          '<div class="oddit-cart-drawer__item-frequency" data-line-plan>' +
            '<span class="oddit-cart-drawer__item-frequency-prefix">Deliver every</span>' +
            '<div class="oddit-cart-drawer__item-frequency-control">' +
              '<select class="oddit-cart-drawer__item-frequency-select" data-frequency-select data-line-key="' + key + '" aria-label="Delivery frequency">' +
                optsHtml +
              '</select>' +
              '<svg class="oddit-cart-drawer__item-frequency-caret" width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</div>' +
          '</div>';
      } else {
        var planName = escapeHtml(allocation.selling_plan.name || 'Subscription');
        planHtml = '<p class="oddit-cart-drawer__item-plan oddit-cart-drawer__item-plan--subscription" data-line-plan>' + planName + '</p>';
      }
    } else if (availablePlan) {
      planHtml = '<p class="oddit-cart-drawer__item-plan oddit-cart-drawer__item-plan--onetime" data-line-plan>One-time purchase</p>';
    } else {
      planHtml = '';
    }

    // On a selling plan with a discount, show the one-time variant price struck
    // through alongside the discounted subscription price (mirrors the Liquid render).
    // cart.js sets original_price == final_price for selling-plan lines, so the
    // original comes from the Liquid-built OdditCartVariantPrices lookup instead.
    var oneTimePrice = (window.OdditCartVariantPrices && item.variant_id != null)
      ? window.OdditCartVariantPrices[item.variant_id]
      : null;
    var discountedPrice = item.final_price != null ? item.final_price : item.price;
    var priceHtml;
    if (hasAllocation && oneTimePrice != null && oneTimePrice > discountedPrice) {
      priceHtml = '<p class="oddit-cart-drawer__item-price">' +
        '<s class="oddit-cart-drawer__item-price-original">' + formatMoney(oneTimePrice) + '</s>' +
        '<span class="oddit-cart-drawer__item-price-discounted">' + price + '</span>' +
      '</p>';
    } else {
      priceHtml = '<p class="oddit-cart-drawer__item-price">' + price + '</p>';
    }

    // Gradient backdrop, mirroring the header product card; white when no metafield.
    var grad = (window.OdditCartGradients && item.product_id != null)
      ? window.OdditCartGradients[item.product_id]
      : null;
    var bgStyle = grad ? ('background: ' + grad + ';') : 'background: #ffffff;';

    return [
      '<article class="oddit-cart-drawer__item" data-line-item data-line-item-key="' + key + '">',
        '<div class="oddit-cart-drawer__item-image-wrap" style="' + bgStyle + '">',
          imageHtml,
        '</div>',
        '<div class="oddit-cart-drawer__item-details">',
          '<div class="oddit-cart-drawer__item-meta">',
            '<h3 class="oddit-cart-drawer__item-name">',
              '<a href="' + url + '">' + title + '</a>',
            '</h3>',
            priceHtml,
            variantHtml,
            planHtml,
          '</div>',
          '<div class="oddit-cart-drawer__item-actions">',
            '<div class="oddit-cart-drawer__quantity" role="group" aria-label="Quantity selector">',
              '<button class="oddit-cart-drawer__quantity-btn" type="button" aria-label="Decrease quantity" data-quantity-action="decrease" data-line-key="' + key + '">',
                '<span aria-hidden="true">&minus;</span>',
              '</button>',
              '<span class="oddit-cart-drawer__quantity-value" aria-live="polite" data-quantity-value>' + Number(item.quantity) + '</span>',
              '<button class="oddit-cart-drawer__quantity-btn" type="button" aria-label="Increase quantity" data-quantity-action="increase" data-line-key="' + key + '">',
                '<span aria-hidden="true">+</span>',
              '</button>',
            '</div>',
            '<button class="oddit-cart-drawer__item-remove" type="button" aria-label="' + removeLabel + '" data-remove-line data-line-key="' + key + '">',
              '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">',
                '<path fill-rule="evenodd" clip-rule="evenodd" d="M6.0795 1.91475C6.14174 1.79019 6.23743 1.68542 6.35586 1.61216C6.47428 1.53891 6.61075 1.50007 6.75 1.5H11.25C11.3892 1.50007 11.5257 1.53891 11.6441 1.61216C11.7626 1.68542 11.8583 1.79019 11.9205 1.91475L13.2135 4.5H15C15.1989 4.5 15.3897 4.57902 15.5303 4.71967C15.671 4.86032 15.75 5.05109 15.75 5.25C15.75 5.44891 15.671 5.63968 15.5303 5.78033C15.3897 5.92098 15.1989 6 15 6H14.25V14.25C14.25 14.8467 14.0129 15.419 13.591 15.841C13.169 16.2629 12.5967 16.5 12 16.5H6C5.40326 16.5 4.83097 16.2629 4.40901 15.841C3.98705 15.419 3.75 14.8467 3.75 14.25V6H3C2.80109 6 2.61032 5.92098 2.46967 5.78033C2.32902 5.63968 2.25 5.44891 2.25 5.25C2.25 5.05109 2.32902 4.86032 2.46967 4.71967C2.61032 4.57902 2.80109 4.5 3 4.5H4.7865L6.0795 1.91475ZM10.7865 3L11.5365 4.5H6.4635L7.2135 3H10.7865ZM8.25 8.25C8.25 8.05109 8.17098 7.86032 8.03033 7.71967C7.88968 7.57902 7.69891 7.5 7.5 7.5C7.30109 7.5 7.11032 7.57902 6.96967 7.71967C6.82902 7.86032 6.75 8.05109 6.75 8.25V12.75C6.75 12.9489 6.82902 13.1397 6.96967 13.2803C7.11032 13.421 7.30109 13.5 7.5 13.5C7.69891 13.5 7.88968 13.421 8.03033 13.2803C8.17098 13.1397 8.25 12.9489 8.25 12.75V8.25ZM11.25 8.25C11.25 8.05109 11.171 7.86032 11.0303 7.71967C10.8897 7.57902 10.6989 7.5 10.5 7.5C10.3011 7.5 10.1103 7.57902 9.96967 7.71967C9.82902 7.86032 9.75 8.05109 9.75 8.25V12.75C9.75 12.9489 9.82902 13.1397 9.96967 13.2803C10.1103 13.421 10.3011 13.5 10.5 13.5C10.6989 13.5 10.8897 13.421 11.0303 13.2803C11.171 13.1397 11.25 12.9489 11.25 12.75V8.25Z" fill="#2A0F3F"/>',
              '</svg>',
            '</button>',
          '</div>',
        '</div>',
      '</article>'
    ].join('');
  }

  function renderLineItems(cart) {
    var containers = qsa('[data-cart-items]', drawer);
    if (!containers.length) return;
    var html = '';
    if (cart && Array.isArray(cart.items)) {
      for (var i = 0; i < cart.items.length; i++) {
        html += buildLineItemHtml(cart.items[i]);
      }
    }
    for (var c = 0; c < containers.length; c++) {
      containers[c].innerHTML = html;
    }
  }

  function updateCounts(cart) {
    var count = (cart && cart.item_count != null) ? cart.item_count : 0;
    var nodes = qsa('.oddit-cart-drawer__count, [data-cart-count], .oddit-cart-drawer__subtotal-count', drawer);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = String(count);
    }
  }

  function updateSubtotal(cart) {
    var total = (cart && cart.total_price != null) ? cart.total_price : 0;
    var nodes = qsa('[data-cart-subtotal]', drawer);
    var formatted = formatMoney(total);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = formatted;
    }
  }

  function toggleCartStates(cart) {
    var hasItems = !!(cart && cart.item_count > 0);

    var filled = qsa('[data-cart-filled]', drawer);
    for (var i = 0; i < filled.length; i++) {
      filled[i].style.display = hasItems ? '' : 'none';
    }

    var empty = qsa('[data-cart-empty]', drawer);
    for (var j = 0; j < empty.length; j++) {
      empty[j].style.display = hasItems ? 'none' : '';
    }

    var footer = qsa('[data-cart-footer]', drawer);
    for (var k = 0; k < footer.length; k++) {
      footer[k].style.display = hasItems ? '' : 'none';
    }

    // Generic state containers, if any are present
    var stateContainers = qsa('[data-cart-state]', drawer);
    for (var s = 0; s < stateContainers.length; s++) {
      var state = stateContainers[s].getAttribute('data-cart-state');
      var show = false;
      if (state === 'empty' && !hasItems) show = true;
      if ((state === 'filled' || state === 'has-items') && hasItems) show = true;
      stateContainers[s].style.display = show ? '' : 'none';
    }
  }

  function filterUpsells(cart) {
    var ids = {};
    if (cart && Array.isArray(cart.items)) {
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.product_id != null) ids[String(item.product_id)] = true;
      }
    }
    var cards = qsa('[data-product-id]', drawer);
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      // Don't hide line items themselves; only upsell/cross-sell/empty cards
      if (card.hasAttribute('data-line-item')) continue;
      var pid = card.getAttribute('data-product-id');
      if (pid && ids[pid]) {
        card.style.display = 'none';
      } else {
        card.style.display = '';
      }
    }
  }

  function updateProgressBar(cart) {
    if (!sectionRoot) return;
    var thresholdCents = parseInt(sectionRoot.getAttribute('data-free-shipping-threshold'), 10) || 0;
    if (thresholdCents <= 0) return;

    var totalCents = (cart && cart.total_price != null) ? cart.total_price : 0;
    var percentage = Math.min((totalCents / thresholdCents) * 100, 100);
    if (percentage < 0) percentage = 0;
    var remainingCents = Math.max(thresholdCents - totalCents, 0);

    var fills = qsa('.oddit-cart-drawer__shipping-fill, [data-shipping-fill]', drawer);
    for (var i = 0; i < fills.length; i++) {
      fills[i].style.width = percentage + '%';
    }

    var texts = qsa('.oddit-cart-drawer__shipping-text, [data-shipping-text]', drawer);
    var message;
    if (remainingCents <= 0) {
      message = 'You&rsquo;ve unlocked <strong>FREE</strong> shipping!';
    } else {
      message = 'You&rsquo;re <strong>' + formatMoney(remainingCents) + '</strong> away from unlocking <strong>FREE</strong> shipping!';
    }
    for (var t = 0; t < texts.length; t++) {
      texts[t].innerHTML = message;
    }

    updateUpsellHeading(remainingCents <= 0);
  }

  function updateUpsellHeading(unlocked) {
    var headings = qsa('[data-upsell-heading]', drawer);
    for (var i = 0; i < headings.length; i++) {
      var h = headings[i];
      var locked = h.getAttribute('data-heading-locked') || '';
      var unlockedText = h.getAttribute('data-heading-unlocked') || '';
      var text = unlocked ? unlockedText : locked;
      if (text) {
        h.textContent = text;
        h.style.display = '';
      } else {
        h.style.display = 'none';
      }
    }
  }

  function renderCart(cart) {
    cartState = cart || null;
    if (cart && cart.currency) {
      activeCurrency = cart.currency;
    }

    renderLineItems(cart);
    updateCounts(cart);
    updateSubtotal(cart);
    updateProgressBar(cart);
    updateSubscriptionToggle(cart);
    toggleCartStates(cart);
    filterUpsells(cart);
    refreshUpsellDots();

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

    maybeHydrateSellingPlans(cart);
  }

  var attemptedPlanHandles = {};

  function applyProductSellingPlans(product) {
    if (!product || !Array.isArray(product.selling_plan_groups) || !product.selling_plan_groups.length) return false;
    var group = null;
    for (var g = 0; g < product.selling_plan_groups.length; g++) {
      if (product.selling_plan_groups[g] && product.selling_plan_groups[g].name === 'Subscription') {
        group = product.selling_plan_groups[g];
        break;
      }
    }
    if (!group) group = product.selling_plan_groups[0];
    if (!group || !Array.isArray(group.selling_plans) || !group.selling_plans.length) return false;

    var options = group.selling_plans.map(function (p) {
      return { id: p.id, label: p.name };
    });
    var defaultPlanId = group.selling_plans[0].id;

    var applied = false;
    var variants = Array.isArray(product.variants) ? product.variants : [];
    for (var v = 0; v < variants.length; v++) {
      var variant = variants[v];
      if (!variant || variant.id == null) continue;
      if (window.OdditCartSellingPlans[variant.id] == null) window.OdditCartSellingPlans[variant.id] = defaultPlanId;
      if (window.OdditCartVariantPrices[variant.id] == null && variant.price != null) window.OdditCartVariantPrices[variant.id] = variant.price;
      if (window.OdditCartSellingPlanOptions[variant.id] == null) window.OdditCartSellingPlanOptions[variant.id] = options.slice();
      applied = true;
    }
    return applied;
  }

  function hydrateMissingSellingPlans(cart) {
    if (!cart || !Array.isArray(cart.items)) return Promise.resolve(false);
    var toFetch = {};
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var vid = it.variant_id;
      if (vid == null) continue;
      var known = (window.OdditCartSellingPlanOptions && window.OdditCartSellingPlanOptions[vid] != null) ||
        (window.OdditCartSellingPlans && window.OdditCartSellingPlans[vid] != null);
      if (known) continue;
      if (!it.handle || attemptedPlanHandles[it.handle]) continue;
      toFetch[it.handle] = true;
    }
    var handles = Object.keys(toFetch);
    if (!handles.length) return Promise.resolve(false);
    handles.forEach(function (h) { attemptedPlanHandles[h] = true; });

    var changed = false;
    var jobs = handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) { if (applyProductSellingPlans(product)) changed = true; })
        .catch(function () {});
    });
    return Promise.all(jobs).then(function () { return changed; });
  }

  function maybeHydrateSellingPlans(cart) {
    hydrateMissingSellingPlans(cart)
      .then(function (changed) {
        if (changed && cartState === cart) renderCart(cart);
      })
      .catch(function () {});
  }

  // ----- Event binding -----------------------------------------------------

  function bindCloseTriggers(signal) {
    var triggers = qsa('[data-cart-drawer-close]', drawer);
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].addEventListener('click', function (e) {
        e.preventDefault();
        close();
      }, { signal: signal });
    }
  }

  function bindOpenTriggers(signal) {
    var openers = qsa('[data-cart-drawer-open]');
    for (var i = 0; i < openers.length; i++) {
      openers[i].addEventListener('click', function (e) {
        e.preventDefault();
        open();
      }, { signal: signal });
    }
  }

  // Returns the selling plan id to apply for a given cart line, or null when the
  // line's variant has no subscription available.
  function planIdForItem(item) {
    if (!item) return null;
    return (window.OdditCartSellingPlans && item.variant_id != null)
      ? (window.OdditCartSellingPlans[item.variant_id] || null)
      : null;
  }

  function itemIsSubscribed(item) {
    return !!(item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);
  }

  function bindSubscriptionToggle(signal) {
    var toggle = qs('.oddit-cart-drawer__toggle, [data-subscription-toggle]', drawer);
    if (!toggle) return;
    toggle.addEventListener('click', function () {
      if (toggle.getAttribute('aria-busy') === 'true') return;
      if (!cartState || !Array.isArray(cartState.items)) return;

      var subscribe = toggle.getAttribute('aria-checked') !== 'true';

      // Collect the line changes needed to reach the desired state.
      var ops = [];
      for (var i = 0; i < cartState.items.length; i++) {
        var item = cartState.items[i];
        var subscribed = itemIsSubscribed(item);
        if (subscribe) {
          var planId = planIdForItem(item);
          if (!subscribed && planId) ops.push({ key: item.key, plan: planId });
        } else if (subscribed) {
          ops.push({ key: item.key, plan: null });
        }
      }
      if (!ops.length) return;

      toggle.setAttribute('aria-busy', 'true');
      // Optimistic flip; reconciled from the server response when the chain resolves.
      toggle.setAttribute('aria-checked', subscribe ? 'true' : 'false');

      // Serialize the cart mutations; render once from the final cart.
      var chain = Promise.resolve(null);
      ops.forEach(function (op) {
        chain = chain.then(function () { return changeSellingPlanRaw(op.key, op.plan); });
      });
      chain
        .then(function (cart) {
          // Clear busy before rendering so updateSubscriptionToggle reconciles
          // the toggle from the authoritative cart response.
          toggle.removeAttribute('aria-busy');
          if (cart) renderCart(cart);
        })
        .catch(function (err) {
          toggle.removeAttribute('aria-busy');
          console.error('[oddit-cart-drawer] subscription toggle failed:', err);
          return refreshCart().catch(function () {});
        });
    }, { signal: signal });
  }

  // Reflect the real cart state on the subscription upsell: check the toggle only
  // when every subscribable line is on a plan, and hide the whole card unless there is
  // at least one one-time purchase left to upgrade.
  function updateSubscriptionToggle(cart) {
    var toggle = qs('.oddit-cart-drawer__toggle, [data-subscription-toggle]', drawer);
    var container = qs('.oddit-cart-drawer__subscription', drawer);
    if (!toggle) return;

    var subscribable = 0;
    var subscribed = 0;
    var oneTimeUpgradeable = 0;
    if (cart && Array.isArray(cart.items)) {
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var isSub = itemIsSubscribed(item);
        if (isSub || planIdForItem(item)) {
          subscribable++;
          if (isSub) subscribed++;
          else oneTimeUpgradeable++;
        }
      }
    }

    if (container) {
      container.style.display = oneTimeUpgradeable > 0 ? '' : 'none';
    }

    // Don't fight an in-flight optimistic state; the toggle handler reconciles on resolve.
    if (toggle.getAttribute('aria-busy') === 'true') return;
    var allSubscribed = subscribable > 0 && subscribed === subscribable;
    toggle.setAttribute('aria-checked', allSubscribed ? 'true' : 'false');
  }

  // The scrollLeft that brings a card to the track's start (behind the 16px
  // scroll-padding), clamped to the scrollable range. Clamping is what makes the
  // last card reachable: its ideal start position sits past maxLeft, so it
  // collapses to maxLeft (the scroll end) instead of an unreachable target.
  function clampedCardTarget(track, card) {
    var trackRect = track.getBoundingClientRect();
    var cardRect = card.getBoundingClientRect();
    var maxLeft = track.scrollWidth - track.clientWidth;
    var target = track.scrollLeft + (cardRect.left - trackRect.left) - 16;
    if (target < 0) target = 0;
    if (target > maxLeft) target = maxLeft;
    return target;
  }

  // Visible upsell cards (filterUpsells() hides any product already in the cart).
  function getVisibleUpsellCards() {
    if (!upsellTrack) return [];
    var all = qsa('.oddit-cart-drawer__upsell-card', upsellTrack);
    var visible = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].style.display !== 'none') visible.push(all[i]);
    }
    return visible;
  }

  function setActiveUpsellDot(index) {
    if (!upsellDotsContainer) return;
    var dots = qsa('.oddit-cart-drawer__upsell-dot', upsellDotsContainer);
    for (var k = 0; k < dots.length; k++) {
      var active = k === index;
      dots[k].classList.toggle('oddit-cart-drawer__upsell-dot--active', active);
      dots[k].setAttribute('aria-selected', active ? 'true' : 'false');
    }
  }

  // Rebuild the dots to match the number of currently-visible cards. Called from
  // renderCart() because filterUpsells() can hide cards (one dot per visible card).
  function refreshUpsellDots() {
    if (!upsellDotsContainer) return;
    var cards = getVisibleUpsellCards();
    upsellDotsContainer.innerHTML = '';
    if (cards.length <= 1) {
      upsellDotsContainer.style.display = 'none';
      return;
    }
    upsellDotsContainer.style.display = '';
    for (var i = 0; i < cards.length; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'oddit-cart-drawer__upsell-dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.setAttribute('data-index', String(i));
      if (i === 0) dot.classList.add('oddit-cart-drawer__upsell-dot--active');
      upsellDotsContainer.appendChild(dot);
    }
  }

  function bindUpsellCarousel(signal) {
    upsellTrack = qs('.oddit-cart-drawer__upsell-track', drawer);
    upsellDotsContainer = qs('[data-upsell-dots]', drawer);
    if (!upsellTrack) return;

    // Dot clicks (delegated so it survives refreshUpsellDots() rebuilds).
    if (upsellDotsContainer) {
      upsellDotsContainer.addEventListener('click', function (e) {
        var dot = e.target.closest('.oddit-cart-drawer__upsell-dot');
        if (!dot) return;
        var index = parseInt(dot.getAttribute('data-index'), 10) || 0;
        var cards = getVisibleUpsellCards();
        setActiveUpsellDot(index);
        if (cards[index]) {
          upsellTrack.scrollTo({ left: clampedCardTarget(upsellTrack, cards[index]), behavior: 'smooth' });
        }
      }, { signal: signal });
    }

    // Active dot follows the scroll position (nearest card to the left edge).
    var scrollTimer = null;
    upsellTrack.addEventListener('scroll', function () {
      if (scrollTimer) return;
      scrollTimer = setTimeout(function () {
        scrollTimer = null;
        var cards = getVisibleUpsellCards();
        if (!cards.length) return;
        var current = upsellTrack.scrollLeft;
        var closest = 0;
        var closestDist = Infinity;
        for (var i = 0; i < cards.length; i++) {
          // Compare each card's clamped target to the current scroll position so
          // the last card (whose target collapses to maxLeft) wins at the end.
          var dist = Math.abs(clampedCardTarget(upsellTrack, cards[i]) - current);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        }
        setActiveUpsellDot(closest);
      }, 80);
    }, { signal: signal });

    // Desktop click-and-drag with smooth snap-to-nearest on release.
    bindDragScroll(upsellTrack, '.oddit-cart-drawer__upsell-card', signal);
  }

  // Empty-state product carousel: dots scroll the track, and the active dot
  // follows the scroll position.
  function bindEmptyDots(signal) {
    var container = qs('[data-empty-dots]', drawer);
    var track = qs('[data-empty-track]', drawer);
    if (!container || !track) return;
    var dots = qsa('.oddit-cart-drawer__empty-dot', container);
    var cards = qsa('.oddit-product-card-cart', track);
    if (!dots.length || !cards.length) return;

    function setActive(index) {
      for (var k = 0; k < dots.length; k++) {
        var isActive = k === index;
        dots[k].classList.toggle('oddit-cart-drawer__empty-dot--active', isActive);
        dots[k].setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    }

    for (var d = 0; d < dots.length; d++) {
      (function (dot, index) {
        dot.addEventListener('click', function () {
          setActive(index);
          if (cards[index]) {
            cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        }, { signal: signal });
      })(dots[d], d);
    }

    var scrollTimer = null;
    track.addEventListener('scroll', function () {
      if (scrollTimer) return;
      scrollTimer = setTimeout(function () {
        scrollTimer = null;
        var trackRect = track.getBoundingClientRect();
        var trackCenter = trackRect.left + trackRect.width / 2;
        var closest = 0;
        var closestDist = Infinity;
        for (var i = 0; i < cards.length; i++) {
          var rect = cards[i].getBoundingClientRect();
          var cardCenter = rect.left + rect.width / 2;
          var dist = Math.abs(cardCenter - trackCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        }
        setActive(closest);
      }, 80);
    }, { signal: signal });
  }

  // Click-and-drag to scroll a snap carousel on desktop (mouse only; touch/pen
  // keep native scrolling). On release it glides smoothly to the nearest card,
  // then restores CSS scroll-snapping. Shared by the empty-state and upsell tracks.
  function bindDragScroll(track, cardSelector, signal) {
    if (!track) return;

    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = false;
    var capturing = false;
    var activePointerId = null;

    // Cards may be <a> links containing <img>; suppress the browser's native
    // drag-and-drop so a press-and-move scrolls instead of starting a ghost drag.
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    }, { signal: signal });

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true;
      moved = false;
      capturing = false;
      activePointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      // Do NOT capture the pointer or mark dragging yet: capturing on a plain
      // press retargets the follow-up `click` to the track, so a card's button
      // or link never receives it. We only capture once a real drag begins.
    }, { signal: signal });

    track.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 3) {
        moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(activePointerId); capturing = true; } catch (err) {}
      }
      if (moved) {
        track.scrollLeft = startScroll - dx;
      }
    }, { signal: signal });

    // Glide smoothly to the nearest card, then hand control back to CSS snapping.
    // While dragging, scroll-snap is off (1:1 tracking); we keep it off until the
    // smooth scroll finishes so the re-snap doesn't cause an abrupt jump.
    function snapToNearest(done) {
      var cards = qsa(cardSelector, track);
      var current = track.scrollLeft;
      var target = current;
      var nearestDist = Infinity;
      var found = false;
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].style.display === 'none') continue;
        // Compare clamped targets so the last card (target == maxLeft) is
        // reachable instead of snapping back to the previous one.
        var candidate = clampedCardTarget(track, cards[i]);
        var dist = Math.abs(candidate - current);
        if (dist < nearestDist) { nearestDist = dist; target = candidate; found = true; }
      }
      if (!found) { if (done) done(); return; }
      if (Math.abs(target - current) < 1) { if (done) done(); return; }

      var finished = false;
      var finish = function () {
        if (finished) return;
        finished = true;
        track.removeEventListener('scrollend', finish);
        if (done) done();
      };
      track.addEventListener('scrollend', finish);
      setTimeout(finish, 500); // fallback for browsers without scrollend
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    track.addEventListener('pointerup', function (e) {
      if (!isDown) return;
      isDown = false;
      if (capturing) {
        try { track.releasePointerCapture(activePointerId); } catch (err) {}
        capturing = false;
      }
      activePointerId = null;
      // If a drag happened, swallow the click so card links/buttons don't fire.
      if (moved) {
        var swallow = function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          track.removeEventListener('click', swallow, true);
        };
        track.addEventListener('click', swallow, true);
        setTimeout(function () {
          track.removeEventListener('click', swallow, true);
        }, 50);
        // keep is-dragging (snap off) during the glide, then restore snapping
        snapToNearest(function () {
          track.classList.remove('is-dragging');
        });
      } else {
        track.classList.remove('is-dragging');
      }
    }, { signal: signal });
  }

  function bindEmptyDrag(signal) {
    var track = qs('[data-empty-track]', drawer);
    bindDragScroll(track, '.oddit-product-card-cart', signal);
  }

  function bindAddToCartButtons(signal) {
    var buttons = qsa('[data-add-to-cart][data-variant-id], .oddit-cart-drawer__rec-add[data-variant-id], .oddit-cart-drawer__upsell-cta[data-variant-id], .oddit-cart-drawer__upsell-add[data-variant-id]', drawer);
    for (var i = 0; i < buttons.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var variantId = btn.getAttribute('data-variant-id');
          if (!variantId) return;
          btn.disabled = true;
          addToCart(Number(variantId))
            .then(function () {
              if (!isOpen) open();
            })
            .catch(function (err) {
              console.error('[oddit-cart-drawer] addToCart failed:', err);
            })
            .then(function () {
              btn.disabled = false;
            });
        }, { signal: signal });
      })(buttons[i]);
    }
  }

  function bindSubscriptionSwitchButtons(signal) {
    var buttons = qsa('.oddit-cart-drawer__switch-cta[data-selling-plan-id]', drawer);
    for (var i = 0; i < buttons.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var sellingPlanId = btn.getAttribute('data-selling-plan-id');
          if (!sellingPlanId || !cartState || !Array.isArray(cartState.items)) return;
          btn.disabled = true;
          var ops = [];
          for (var k = 0; k < cartState.items.length; k++) {
            ops.push(updateLineItemToSubscription(cartState.items[k].key, Number(sellingPlanId)));
          }
          Promise.all(ops)
            .catch(function (err) {
              console.error('[oddit-cart-drawer] subscription switch failed:', err);
            })
            .then(function () {
              btn.disabled = false;
            });
        }, { signal: signal });
      })(buttons[i]);
    }
  }

  function bindCheckoutButton(signal) {
    var checkout = qs('.oddit-cart-drawer__checkout-cta', drawer);
    if (!checkout) return;
    checkout.addEventListener('click', function (e) {
      // Only override if not part of a real form submit that already targets /checkout
      e.preventDefault();
      window.location.href = '/checkout';
    }, { signal: signal });
  }

  function bindLineItemDelegation(signal) {
    var containers = qsa('[data-cart-items]', drawer);
    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];

      container.addEventListener('click', function (e) {
        var qtyBtn = e.target.closest('[data-quantity-action][data-line-key]');
        if (qtyBtn) {
          e.preventDefault();
          var key = qtyBtn.getAttribute('data-line-key');
          var action = qtyBtn.getAttribute('data-quantity-action');
          var lineItem = qtyBtn.closest('[data-line-item]');
          var valueEl = lineItem ? lineItem.querySelector('[data-quantity-value], .oddit-cart-drawer__quantity-value') : null;
          var current = valueEl ? parseInt(valueEl.textContent, 10) || 1 : 1;
          var next = action === 'decrease' ? Math.max(current - 1, 0) : current + 1;
          qtyBtn.disabled = true;
          updateLineItemQuantity(key, next)
            .catch(function (err) {
              console.error('[oddit-cart-drawer] quantity update failed:', err);
            })
            .then(function () {
              qtyBtn.disabled = false;
            });
          return;
        }

        // Backward-compat: handle legacy [data-quantity-decrease]/[data-quantity-increase]
        var legacyDec = e.target.closest('[data-quantity-decrease]');
        var legacyInc = e.target.closest('[data-quantity-increase]');
        if (legacyDec || legacyInc) {
          e.preventDefault();
          var legacyItem = (legacyDec || legacyInc).closest('[data-line-item]');
          if (!legacyItem) return;
          var legacyKey = legacyItem.getAttribute('data-line-item-key');
          var lvEl = legacyItem.querySelector('[data-quantity-value], .oddit-cart-drawer__quantity-value');
          var lvCurrent = lvEl ? parseInt(lvEl.textContent, 10) || 1 : 1;
          var lvNext = legacyDec ? Math.max(lvCurrent - 1, 0) : lvCurrent + 1;
          updateLineItemQuantity(legacyKey, lvNext).catch(function (err) {
            console.error('[oddit-cart-drawer] legacy quantity update failed:', err);
          });
          return;
        }

        var removeBtn = e.target.closest('[data-remove-line][data-line-key], [data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          var removeKey = removeBtn.getAttribute('data-line-key');
          if (!removeKey) {
            var parent = removeBtn.closest('[data-line-item]');
            removeKey = parent ? parent.getAttribute('data-line-item-key') : null;
          }
          if (!removeKey) return;
          removeBtn.disabled = true;
          removeFromCart(removeKey)
            .catch(function (err) {
              console.error('[oddit-cart-drawer] remove failed:', err);
            })
            .then(function () {
              removeBtn.disabled = false;
            });
        }
      }, { signal: signal });

      container.addEventListener('change', function (e) {
        var sel = e.target.closest('[data-frequency-select][data-line-key]');
        if (!sel) return;
        var key = sel.getAttribute('data-line-key');
        var planId = sel.value;
        if (!key || !planId) return;
        sel.disabled = true;
        updateLineItemToSubscription(key, Number(planId))
          .catch(function (err) {
            console.error('[oddit-cart-drawer] frequency change failed:', err);
            return refreshCart().catch(function () {});
          })
          .then(function () {
            sel.disabled = false;
          });
      }, { signal: signal });
    }
  }

  function bindOverlayClick(signal) {
    var overlay = qs('.oddit-cart-drawer__overlay', drawer);
    if (!overlay) return;
    overlay.addEventListener('click', function () {
      close();
    }, { signal: signal });
  }

  function bindKeydown(signal) {
    document.addEventListener('keydown', onKeydown, { signal: signal });
  }

  // ----- Lifecycle ---------------------------------------------------------

  function init() {
    drawer = qs(SECTION_SELECTOR);
    if (!drawer) return;
    sectionRoot = drawer;

    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();
    var signal = abortController.signal;

    bindCloseTriggers(signal);
    bindOpenTriggers(signal);
    bindOverlayClick(signal);
    bindKeydown(signal);
    bindSubscriptionToggle(signal);
    bindUpsellCarousel(signal);
    bindEmptyDots(signal);
    bindEmptyDrag(signal);
    bindAddToCartButtons(signal);
    bindSubscriptionSwitchButtons(signal);
    bindCheckoutButton(signal);
    bindLineItemDelegation(signal);

    // Fetch the canonical cart state on init so the drawer is in sync with the server
    getCart()
      .then(function (cart) {
        renderCart(cart);
      })
      .catch(function (err) {
        console.error('[oddit-cart-drawer] initial getCart failed:', err);
      });
  }

  function destroy() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (isOpen) {
      unlockBodyScroll();
      isOpen = false;
    }
    drawer = null;
    sectionRoot = null;
  }

  // ----- Public API --------------------------------------------------------

  window.OdditCartDrawer = {
    open: open,
    close: close,
    toggle: toggle,
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart,
    init: init,
    destroy: destroy
  };

  // Theme editor compatibility
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector(SECTION_SELECTOR)) {
      destroy();
      init();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector(SECTION_SELECTOR)) {
      destroy();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();